import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import roleService from '../../services/roleService';
import './Roles.css';

const RolesManagement = () => {
  const { user } = useContext(AuthContext);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 5,
    totalPages: 0
  });

  // Helper function to check if user has permission
  const hasPermission = (permission) => {
    if (user?.roles?.some(r => ['admin', 'manager'].includes(r.name))) {
      return true;
    }
    return user?.permissions?.some(p => p.name === permission);
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesRes, permissionsRes] = await Promise.all([
        roleService.getAllRoles({ page: currentPage, limit: 5, search: searchTerm }),
        roleService.getAllPermissions()
      ]);

      if (rolesRes.success) {
        setRoles(rolesRes.data?.roles || []);
        setPagination(rolesRes.data?.pagination || { total: 0, page: 1, limit: 5, totalPages: 0 });
      }
      if (permissionsRes.success) {
        setPermissions(permissionsRes.data?.permissions || permissionsRes.data || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < pagination.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCreateNew = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      isActive: true
    });
    setShowModal(true);
  };

  const handleEdit = (roleToEdit) => {
    setEditingRole(roleToEdit);
    setFormData({
      name: roleToEdit.name,
      description: roleToEdit.description || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      let response;
      if (editingRole) {
        response = await roleService.updateRole(editingRole.id, formData);
      } else {
        response = await roleService.createRole(formData);
      }

      if (response.success) {
        setSuccessMessage(editingRole ? 'Role updated successfully!' : 'Role created successfully!');
        setShowModal(false);
        setEditingRole(null);
        resetForm();
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error(editingRole ? 'Update role error:' : 'Create role error:', err);
      let errorMessage = editingRole ? 'Failed to update role' : 'Failed to create role';
      if (err.errors && Array.isArray(err.errors)) {
        errorMessage = err.errors.map(e => e.msg).join(', ');
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    }
  };

  const handleManagePermissions = async (roleToManage) => {
    try {
      setLoading(true);
      const roleDetailsRes = await roleService.getRoleById(roleToManage.id);

      if (roleDetailsRes.success) {
        const roleWithPermissions = roleDetailsRes.data?.role;
        setSelectedRole(roleWithPermissions);
        setSelectedPermissions(roleWithPermissions.permissions?.map(p => p.id) || []);
        setShowPermissionModal(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch role permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSavePermissions = async () => {
    setError('');
    setSuccessMessage('');

    try {
      const currentPermissionIds = selectedRole.permissions?.map(p => p.id) || [];
      const permissionsToAdd = selectedPermissions.filter(id => !currentPermissionIds.includes(id));
      const permissionsToRemove = currentPermissionIds.filter(id => !selectedPermissions.includes(id));

      // Add new permissions
      for (const permissionId of permissionsToAdd) {
        await roleService.assignPermissionToRole(selectedRole.id, permissionId);
      }

      // Remove old permissions
      for (const permissionId of permissionsToRemove) {
        await roleService.removePermissionFromRole(selectedRole.id, permissionId);
      }

      setSuccessMessage('Permissions updated successfully!');
      setShowPermissionModal(false);
      fetchData();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Update permissions error:', err);
      setError(err.message || 'Failed to update permissions');
    }
  };

  const handleDelete = async (roleToDelete) => {
    if (!window.confirm(`Are you sure you want to delete the role "${roleToDelete.name}"?`)) {
      return;
    }

    try {
      const response = await roleService.deleteRole(roleToDelete.id);
      if (response.success) {
        setSuccessMessage('Role deleted successfully!');
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Delete role error:', err);
      setError(err.message || 'Failed to delete role');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: ''
    });
  };

  if (!hasPermission('roles.read')) {
    return (
      <div className="roles-management">
        <div className="section-header">
          <h2>Access Denied</h2>
        </div>
        <div className="error-message">
          You need the 'roles.read' permission to access Role Management.
        </div>
      </div>
    );
  }

  return (
    <div className="roles-management">
      <div className="section-header">
        <div>
          <h2>Role Management</h2>
          <p>Manage roles and their permissions</p>
        </div>
        {hasPermission('roles.create') && (
          <button className="btn-create" onClick={handleCreateNew}>
            + Create Role
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search by role name or description..."
          value={searchTerm}
          onChange={handleSearch}
        />
        <span className="search-icon">🔍</span>
      </div>

      {loading ? (
        <div className="loading">Loading roles...</div>
      ) : (
        <>
          <div className="roles-table-container">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Role Name</th>
                  <th>Description</th>
                  <th>Permissions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="no-data">No roles found</td>
                  </tr>
                ) : (
                  roles.map(role => (
                    <tr key={role.id}>
                      <td><strong>{role.name}</strong></td>
                      <td>{role.description || 'No description'}</td>
                      <td>
                        <span className="permission-count">
                          {role.permission_count || 0} permission{role.permission_count !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          {hasPermission('roles.update') && (
                            <>
                              <button
                                className="btn-icon btn-edit"
                                onClick={() => handleEdit(role)}
                                title="Edit Role"
                              >
                                ✏️
                              </button>
                              <button
                                className="btn-icon btn-permissions"
                                onClick={() => handleManagePermissions(role)}
                                title="Manage Permissions"
                              >
                                🔒
                              </button>
                            </>
                          )}
                          {hasPermission('roles.delete') && (
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(role)}
                              title="Delete Role"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 0 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {roles.length} of {pagination.total} roles
                {searchTerm && ` (filtered by "${searchTerm}")`}
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  ◀ Previous
                </button>
                <span className="page-indicator">
                  Page {currentPage} of {pagination.totalPages}
                </span>
                <button
                  className="pagination-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === pagination.totalPages}
                >
                  Next ▶
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Role Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRole ? 'Edit Role' : 'Create New Role'}</h3>
              <button className="close-button" onClick={() => { setShowModal(false); setEditingRole(null); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Role Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Manager, Analyst, etc."
                  />
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Describe the role's responsibilities..."
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); setEditingRole(null); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Permissions Modal */}
      {showPermissionModal && selectedRole && (
        <div className="modal-overlay" onClick={() => setShowPermissionModal(false)}>
          <div className="modal-content permissions-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Permissions - {selectedRole.name}</h3>
              <button className="close-button" onClick={() => setShowPermissionModal(false)}>×</button>
            </div>
            <div className="modal-search">
              <input
                type="text"
                placeholder="Search permissions..."
                value={permissionSearchTerm}
                onChange={(e) => setPermissionSearchTerm(e.target.value)}
              />
            </div>
            <div className="permissions-selection">
              {permissions.length === 0 ? (
                <p className="no-data">No permissions available</p>
              ) : (
                permissions
                  .filter(permission =>
                    permission.name.toLowerCase().includes(permissionSearchTerm.toLowerCase()) ||
                    permission.resource?.toLowerCase().includes(permissionSearchTerm.toLowerCase()) ||
                    permission.action?.toLowerCase().includes(permissionSearchTerm.toLowerCase()) ||
                    permission.description?.toLowerCase().includes(permissionSearchTerm.toLowerCase())
                  )
                  .map(permission => (
                  <div key={permission.id} className="permission-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission.id)}
                        onChange={() => handlePermissionToggle(permission.id)}
                      />
                      <div className="permission-info">
                        <strong>{permission.name}</strong>
                        <small>{permission.description || `${permission.resource} - ${permission.action}`}</small>
                      </div>
                    </label>
                  </div>
                ))
              )}
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowPermissionModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn-submit" onClick={handleSavePermissions}>
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesManagement;
